import React, { useState, useEffect } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import ChecklistSection from './components/ChecklistSection';
import { CHECKLIST } from './checklist';
import FullScreenButton from './FullScreenButton';
import WelcomeScreen from './WelcomeScreen';

const TOTAL = CHECKLIST.length;

const DEFAULT_CC = [
  'vivekananda.naskar@protegrity.com',
  'steven.kim@protegrity.com',
  'silviu.chirila@protegrity.com',
  'william.ramirez@protegrity.com',
];

// Azure Logic App endpoint that writes a row to the SharePoint Onboarding-Log.xlsx.
// Paste your Logic App "HTTP POST URL" between the quotes below.
// SECURITY NOTE: this URL ends up in the public browser bundle. See Rahul's chat
// for how to move it behind a build-time secret / backend later (Phase 8).
const LOGIC_APP_URL = 'https://prod-26.northcentralus.logic.azure.com:443/workflows/c59421ec6f2144cfabdacd739bac8004/triggers/When_an_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_an_HTTP_request_is_received%2Frun&sv=1.0&sig=qtZcuh7oDgzI577vWQWY541qHAP6MQCr61WvndzsqGo';

export default function App() {
  const [os, setOs] = useState('win');
  // Welcome / device-selection screen shows until the user picks a device.
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sectionStatus, setSectionStatus] = useState(() => {
    const s = {};
    CHECKLIST.forEach((sec, i) => { s[sec.id] = i === 0 ? 'active' : 'pending'; });
    return s;
  });
  const [followUpNotes, setFollowUpNotes] = useState([]);
  const [skippedSections, setSkippedSections] = useState([]);
  const [userName, setUserName] = useState('New Joiner');
  const [userEmail, setUserEmail] = useState('');
  const [ccList, setCcList] = useState(DEFAULT_CC.join(', '));
  const [feedback, setFeedback] = useState('');
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sessionStart, setSessionStart] = useState(() => Date.now());
  const [sessionId, setSessionId] = useState(0);

  useEffect(() => {
    microsoftTeams.app.initialize().then(() => {
      microsoftTeams.app.getContext().then(ctx => {
        if (ctx?.user?.displayName) setUserName(ctx.user.displayName.split(' ')[0]);
        if (ctx?.user?.userPrincipalName) setUserEmail(ctx.user.userPrincipalName);
      });
    }).catch(() => {
      console.log('Running outside Teams — dev mode');
    });
  }, []);

  const completedCount = Object.values(sectionStatus).filter(s => s === 'done').length;
  const progressPct = Math.round((completedCount / TOTAL) * 100);

  function handleComplete(sectionId) {
    setSectionStatus(prev => {
      const next = { ...prev, [sectionId]: 'done' };
      const nextId = sectionId + 1;
      if (nextId <= TOTAL && next[nextId] === 'pending') {
        next[nextId] = 'active';
      }
      return next;
    });

    // Smooth scroll so the NEXT section's header lands just below the sticky
    // header. Wait for the collapse/expand animations to settle, then compute
    // the position fresh and offset by the sticky header height.
    setTimeout(() => {
      const nextEl = document.getElementById(`section-${sectionId + 1}`);
      if (nextEl) {
        const headerOffset = 175;
        const y = nextEl.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 450);

    if (sectionId === TOTAL) setShowEmailPanel(true);
  }

  function handleSkip(sectionId) {
    setSkippedSections(prev => [...prev, sectionId]);
    handleComplete(sectionId);
  }

  function handleNote(sectionId, sectionTitle, note) {
    setFollowUpNotes(prev => [...prev, { sectionId, sectionTitle, note }]);
  }

  // Clears all session state. Used by Reset and by device re-selection.
  function clearSession() {
    const s = {};
    CHECKLIST.forEach((sec, i) => { s[sec.id] = i === 0 ? 'active' : 'pending'; });
    setSectionStatus(s);
    setFollowUpNotes([]);
    setSkippedSections([]);
    setFeedback('');
    setShowEmailPanel(false);
    setEmailSent(false);
    setSessionStart(Date.now());
    setSessionId(id => id + 1);
  }

  function resetSession() {
    if (!window.confirm('Reset session and start over? This clears all progress.')) return;
    clearSession();
  }

  // Back to the welcome screen. Warns first if there is progress to lose.
  function backToDeviceSelection() {
    const covered = CHECKLIST.filter(sec => sectionStatus[sec.id] === 'done').length;
    if (covered > 0 && !window.confirm(
      'Go back to device selection?\n\nThis starts a new session and clears all progress, notes and feedback.'
    )) return;
    setSessionStarted(false);
  }

  const allDone = CHECKLIST.every(sec => sectionStatus[sec.id] === 'done');

  // Fire-and-forget: write one audit row to SharePoint Excel via the Logic App.
  function logToSharePoint() {
    if (!LOGIC_APP_URL || LOGIC_APP_URL.includes('PASTE_')) {
      console.log('Logic App URL not set — skipping SharePoint log');
      return;
    }
    const skipped = skippedSections.length
      ? skippedSections.map(id => CHECKLIST.find(s => s.id === id)?.title).filter(Boolean).join(', ')
      : 'None';
    const notes = followUpNotes.length
      ? followUpNotes.map(n => `${n.sectionTitle}: ${n.note}`).join(' | ')
      : 'None';
    const durationMin = Math.max(1, Math.round((Date.now() - sessionStart) / 60000));
    const payload = {
      Timestamp: new Date().toLocaleString(),
      NewJoinerName: userName || 'New Joiner',
      NewJoinerEmail: userEmail || '',
      CC: ccList || '',
      OS: os === 'mac' ? 'Mac' : 'Windows',
      SectionsCovered: `${completedCount} of ${TOTAL}`,
      PercentComplete: `${progressPct}%`,
      SectionsSkipped: skipped,
      Notes: notes,
      Duration: `${durationMin} min`,
      Status: progressPct === 100 ? 'Complete' : 'Partial',
      Feedback: feedback.trim(),
    };
    // text/plain + no-cors keeps the browser from blocking the cross-origin call.
    fetch(LOGIC_APP_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
    })
      .then(() => console.log('Onboarding logged to SharePoint'))
      .catch(e => console.log('SharePoint logging failed (non-blocking):', e));
  }

  function handleSendEmail() {
    if (!userEmail.trim()) {
      alert('Please enter the new joiner\'s email address');
      return;
    }
    // Feedback is mandatory — the email cannot be sent without it.
    if (!feedback.trim()) {
      alert('Please enter session feedback before sending the summary email');
      const el = document.getElementById('feedback-field');
      if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return;
    }
    // Log this session to the SharePoint audit sheet (does not block the email).
    logToSharePoint();
    // In production this calls Microsoft Graph API via Lambda
    setEmailSent(true);
    alert(`✅ Summary email queued\n\nTO: ${userEmail}\nCC: ${ccList}\n\nFeedback: ${feedback.trim()}\n\nIn production this will use Microsoft Graph API to send the email using the standard Protegrity template.`);
  }

  // Welcome screen — shown before the checklist until a device is chosen.
  if (!sessionStarted) {
    return (
      <WelcomeScreen
        onSelect={chosenOs => {
          // Picking a device always begins a fresh session from section 1.
          setOs(chosenOs);
          clearSession();
          setSessionStarted(true);
        }}
      />
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: '100vh',
      background: '#f3f2f1', fontFamily: "'Segoe UI', -apple-system, sans-serif",
    }}>

      <FullScreenButton />

      {/* Sticky top zone — logo banner + header stay pinned while scrolling */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, flexShrink: 0 }}>

      {/* Presenting banner — charcoal brand surface with centred logo */}
      <div style={{
        background: '#1C1F24',
        padding: '10px 14px', color: '#fff',
        fontSize: 14, fontWeight: 600,
        display: 'flex', alignItems: 'center',
        flexShrink: 0, letterSpacing: 0.3,
        position: 'relative', minHeight: 48,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', marginRight: 7,
          background: '#82D222', boxShadow: '0 0 8px #82D222',
          animation: 'pulse 1.5s ease-in-out infinite',
          display: 'inline-block', flexShrink: 0,
        }} />
        <span style={{ whiteSpace: 'nowrap' }}>AGENT IS GUIDING THIS ONBOARDING — SHARED SCREEN</span>

        {/* Protegrity logo — full-colour lockup on the charcoal brand surface */}
        <img
          src="/protegrity-logo.png"
          alt="Protegrity"
          style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            height: 38, width: 'auto', pointerEvents: 'none',
          }}
        />
      </div>

      {/* Header */}
      <div style={{
        background: '#009245', padding: '14px 14px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17.5, fontWeight: 700, color: '#fff' }}>
              IT Onboarding Checklist
            </div>
            <div style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.9)', marginTop: 2 }}>
              Welcome, {userName} · Protegrity
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={backToDeviceSelection}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4,
                padding: '4px 8px', fontSize: 12.5, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              title="Change device — starts a new session"
            >
              ← Device
            </button>
            <button
              onClick={resetSession}
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4,
                padding: '4px 8px', fontSize: 12.5, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              title="Reset session"
            >
              ↻ Reset
            </button>
          </div>
        </div>

        {/* Selected device — chosen on the welcome screen, change via ← Device */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 10,
          background: 'rgba(255,255,255,0.15)', borderRadius: 5,
          padding: '5px 12px', fontSize: 14.5, fontWeight: 600, color: '#fff',
        }}>
          <span>{os === 'win' ? '🖥️' : '💻'}</span>
          <span>
            Showing steps for {os === 'win' ? 'Windows laptop' : 'MacBook'}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{
          background: 'rgba(255,255,255,0.2)', height: 6,
          borderRadius: 3, marginTop: 10, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', background: '#82D222', borderRadius: 3,
            width: `${progressPct}%`, transition: 'width 0.5s ease',
            boxShadow: '0 0 8px rgba(127,255,127,0.5)',
          }} />
        </div>
        <div style={{
          fontSize: 13, color: 'rgba(255,255,255,0.95)',
          marginTop: 5, display: 'flex', justifyContent: 'space-between',
        }}>
          <span>{completedCount} of {TOTAL} topics covered</span>
          {followUpNotes.length > 0 && (
            <span>📝 {followUpNotes.length} note{followUpNotes.length > 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      </div>{/* end sticky top zone */}

      {/* Sections */}
      <div style={{
        flex: 1, padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {CHECKLIST.map((section, idx) => (
          <div key={`${sessionId}-${section.id}`} id={`section-${section.id}`}>
            <ChecklistSection
              section={section}
              sectionNumber={idx + 1}
              totalSections={TOTAL}
              status={sectionStatus[section.id]}
              os={os}
              onComplete={handleComplete}
              onNote={handleNote}
              onSkip={handleSkip}
            />
          </div>
        ))}

        {/* Email panel — appears when all done */}
        {(allDone || showEmailPanel) && (
          <div style={{
            background: '#fff', border: '2px solid #009245',
            borderRadius: 8, padding: '16px 14px', marginTop: 8,
            boxShadow: '0 4px 16px rgba(98,100,167,0.2)',
          }}>
            <div style={{ fontSize: 19, fontWeight: 700, color: '#009245', marginBottom: 10 }}>
              📧 Send Summary Email
            </div>

            {emailSent ? (
              <div style={{
                background: '#f0faf0', border: '1.5px solid #107c10',
                borderRadius: 5, padding: '12px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 30 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#107c10', marginTop: 4 }}>
                  Email sent successfully!
                </div>
                <div style={{ fontSize: 14, color: '#605e5c', marginTop: 4 }}>
                  Onboarding complete. Have a great day!
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 15, color: '#605e5c', marginBottom: 12, lineHeight: 1.5 }}>
                  All topics covered. Review the recipients and send the summary email to the new joiner.
                </div>

                {/* TO field */}
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#605e5c', marginBottom: 4 }}>
                    TO <span style={{ color: '#d83b01' }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    placeholder="newjoiner@protegrity.com"
                    style={{
                      width: '100%', padding: '8px 11px', fontSize: 15,
                      border: '1.5px solid #c8c6c4', borderRadius: 5,
                      fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* CC field */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#605e5c', marginBottom: 4 }}>
                    CC <span style={{ fontWeight: 400, color: '#8a8886' }}>(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={ccList}
                    onChange={e => setCcList(e.target.value)}
                    placeholder="manager@protegrity.com, team@protegrity.com"
                    style={{
                      width: '100%', padding: '8px 11px', fontSize: 15,
                      border: '1.5px solid #c8c6c4', borderRadius: 5,
                      fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Feedback field — mandatory */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: '#605e5c', marginBottom: 4 }}>
                    FEEDBACK <span style={{ color: '#d83b01' }}>*</span>{' '}
                    <span style={{ fontWeight: 400, color: '#8a8886' }}>(required)</span>
                  </label>
                  <textarea
                    id="feedback-field"
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder="How did the onboarding session go? Any issues, questions, or things to improve?"
                    rows={4}
                    style={{
                      width: '100%', padding: '8px 11px', fontSize: 15,
                      border: `1.5px solid ${feedback.trim() ? '#c8c6c4' : '#d83b01'}`,
                      borderRadius: 5, fontFamily: 'inherit', outline: 'none',
                      boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5,
                    }}
                  />
                  {!feedback.trim() && (
                    <div style={{ fontSize: 13, color: '#d83b01', marginTop: 4 }}>
                      Feedback is required before the summary email can be sent.
                    </div>
                  )}
                </div>

                {/* Notes summary */}
                {followUpNotes.length > 0 && (
                  <div style={{
                    background: '#fff4ce', borderRadius: 5, padding: '10px 12px', marginBottom: 10,
                    border: '1px solid #f0c389',
                  }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: '#7a5c00', marginBottom: 5 }}>
                      📝 {followUpNotes.length} follow-up note{followUpNotes.length > 1 ? 's' : ''} — included in email
                    </div>
                    {followUpNotes.map((n, i) => (
                      <div key={i} style={{ fontSize: 14, color: '#7a5c00', marginTop: 4, lineHeight: 1.4 }}>
                        • <strong>{n.sectionTitle}:</strong> {n.note}
                      </div>
                    ))}
                  </div>
                )}

                {/* Skipped */}
                {skippedSections.length > 0 && (
                  <div style={{
                    background: '#e1f3ff', borderRadius: 5, padding: '10px 12px', marginBottom: 10,
                    fontSize: 14, color: '#005a9e', border: '1px solid #b8daff',
                  }}>
                    <strong>⏭️ Skipped sections:</strong> {skippedSections.map(id => CHECKLIST.find(s => s.id === id)?.title).join(', ')} — instructions will be emailed.
                  </div>
                )}

                {/* Send button */}
                <button
                  onClick={handleSendEmail}
                  disabled={!feedback.trim()}
                  style={{
                    width: '100%', padding: '12px 0', fontSize: 16, fontWeight: 700,
                    background: feedback.trim() ? '#107c10' : '#c8c6c4',
                    color: '#fff', border: 'none',
                    borderRadius: 6, cursor: feedback.trim() ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                  }}
                >
                  ✉️ Send Summary Email
                </button>

                <div style={{ fontSize: 12.5, color: '#8a8886', textAlign: 'center', marginTop: 6 }}>
                  Uses standard Protegrity onboarding template
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 12px', borderTop: '1px solid #e1dfdd',
        background: '#fff', flexShrink: 0,
      }}>
        <div style={{ fontSize: 12.5, color: '#8a8886', textAlign: 'center' }}>
          Guidance only — IT staff & user perform all actions • corp.infosec@protegrity.com
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
